import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Calculator, 
  Home,
  DollarSign,
  Percent,
  Calendar,
  PiggyBank,
  TrendingUp,
  Loader2
} from "lucide-react";
import Navbar from "../components/Navbar";
import { API } from "../App";

const MortgageCalculator = () => {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [homePrice, setHomePrice] = useState(500000);
  const [downPayment, setDownPayment] = useState(100000);
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  const [interestRate, setInterestRate] = useState(6.5);
  const [loanTerm, setLoanTerm] = useState("30");
  const [propertyTax, setPropertyTax] = useState(2500);
  const [insurance, setInsurance] = useState(1200);
  const [minPrice, setMinPrice] = useState(100000);
  const [maxPrice, setMaxPrice] = useState(5000000);
  const [loanTermOptions, setLoanTermOptions] = useState(["15", "20", "30"]);

  useEffect(() => {
    fetch(`${API}/settings/business`)
      .then((r) => r.json())
      .then((data) => {
        const rate = data.calc_default_interest_rate ?? 6.5;
        const dpPct = data.calc_default_down_payment_pct ?? 20;
        const tax = data.calc_default_property_tax ?? 2500;
        const ins = data.calc_default_insurance ?? 1200;
        const min = data.calc_min_home_price ?? 100000;
        const max = data.calc_max_home_price ?? 5000000;
        const terms = data.calc_loan_terms || "15,20,30";

        setInterestRate(rate);
        setDownPaymentPercent(dpPct);
        setPropertyTax(tax);
        setInsurance(ins);
        setMinPrice(min);
        setMaxPrice(max);
        setDownPayment(Math.round(homePrice * dpPct / 100));

        const termsList = terms.split(",").map((t) => t.trim()).filter(Boolean);
        setLoanTermOptions(termsList);
        if (!termsList.includes(loanTerm)) setLoanTerm(termsList[termsList.length - 1] || "30");
      })
      .catch(() => {})
      .finally(() => setConfigLoaded(true));
  }, []);

  const loanAmount = homePrice - downPayment;
  const monthlyRate = interestRate / 100 / 12;
  const numberOfPayments = parseInt(loanTerm) * 12;

  // Calculate monthly mortgage payment using standard formula
  const calculateMonthlyPayment = () => {
    if (monthlyRate === 0) {
      return loanAmount / numberOfPayments;
    }
    const payment = loanAmount * 
      (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    return payment;
  };

  const monthlyMortgage = calculateMonthlyPayment();
  const monthlyTax = propertyTax / 12;
  const monthlyInsurance = insurance / 12;
  const totalMonthly = monthlyMortgage + monthlyTax + monthlyInsurance;
  const totalPayments = totalMonthly * numberOfPayments;
  const totalInterest = (monthlyMortgage * numberOfPayments) - loanAmount;

  const handleHomePriceChange = (value) => {
    setHomePrice(value);
    setDownPayment(Math.round(value * downPaymentPercent / 100));
  };

  const handleDownPaymentChange = (value) => {
    setDownPayment(value);
    setDownPaymentPercent(Math.round((value / homePrice) * 100));
  };

  const handleDownPaymentPercentChange = (value) => {
    setDownPaymentPercent(value);
    setDownPayment(Math.round(homePrice * value / 100));
  };

  const formatCurrency = (value) => {
    return `K${value.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-12 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 bg-primary/10 rounded-sm flex items-center justify-center mx-auto mb-4">
              <Calculator className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl md:text-4xl font-serif mb-4">Mortgage Calculator</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Calculate your estimated monthly mortgage payment and see how different factors 
              affect your home buying budget.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Inputs */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-card border border-border rounded-sm p-6 space-y-8"
            >
              {/* Home Price */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center gap-2 font-medium">
                    <Home className="w-4 h-4 text-primary" />
                    Home Price
                  </label>
                  <span className="font-mono text-lg font-semibold">{formatCurrency(homePrice)}</span>
                </div>
                <Slider
                  value={[homePrice]}
                  onValueChange={([val]) => handleHomePriceChange(val)}
                  min={minPrice}
                  max={maxPrice}
                  step={10000}
                  className="mb-2"
                  data-testid="home-price-slider"
                />
                <Input
                  type="number"
                  value={homePrice}
                  onChange={(e) => handleHomePriceChange(parseInt(e.target.value) || 0)}
                  className="h-10"
                  data-testid="home-price-input"
                />
              </div>

              {/* Down Payment */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center gap-2 font-medium">
                    <PiggyBank className="w-4 h-4 text-primary" />
                    Down Payment
                  </label>
                  <span className="font-mono text-lg font-semibold">
                    {formatCurrency(downPayment)} ({downPaymentPercent}%)
                  </span>
                </div>
                <Slider
                  value={[downPaymentPercent]}
                  onValueChange={([val]) => handleDownPaymentPercentChange(val)}
                  min={0}
                  max={50}
                  step={1}
                  className="mb-2"
                  data-testid="down-payment-slider"
                />
                <Input
                  type="number"
                  value={downPayment}
                  onChange={(e) => handleDownPaymentChange(parseInt(e.target.value) || 0)}
                  className="h-10"
                  data-testid="down-payment-input"
                />
              </div>

              {/* Interest Rate */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center gap-2 font-medium">
                    <Percent className="w-4 h-4 text-primary" />
                    Interest Rate
                  </label>
                  <span className="font-mono text-lg font-semibold">{interestRate}%</span>
                </div>
                <Slider
                  value={[interestRate]}
                  onValueChange={([val]) => setInterestRate(val)}
                  min={1}
                  max={15}
                  step={0.125}
                  className="mb-2"
                  data-testid="interest-rate-slider"
                />
                <Input
                  type="number"
                  step="0.125"
                  value={interestRate}
                  onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
                  className="h-10"
                  data-testid="interest-rate-input"
                />
              </div>

              {/* Loan Term */}
              <div>
                <label className="flex items-center gap-2 font-medium mb-3">
                  <Calendar className="w-4 h-4 text-primary" />
                  Loan Term
                </label>
                <Select value={loanTerm} onValueChange={setLoanTerm}>
                  <SelectTrigger className="h-11" data-testid="loan-term-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {loanTermOptions.map((term) => (
                      <SelectItem key={term} value={term}>{term} Years</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Additional Costs */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Annual Property Tax</label>
                  <Input
                    type="number"
                    value={propertyTax}
                    onChange={(e) => setPropertyTax(parseInt(e.target.value) || 0)}
                    className="h-10"
                    data-testid="property-tax-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Annual Insurance</label>
                  <Input
                    type="number"
                    value={insurance}
                    onChange={(e) => setInsurance(parseInt(e.target.value) || 0)}
                    className="h-10"
                    data-testid="insurance-input"
                  />
                </div>
              </div>
            </motion.div>

            {/* Results */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="space-y-6"
            >
              {/* Monthly Payment Card */}
              <div className="bg-primary text-white rounded-sm p-8 text-center">
                <p className="text-white/80 mb-2">Estimated Monthly Payment</p>
                <p className="font-mono text-5xl font-bold mb-4" data-testid="monthly-payment">
                  {formatCurrency(totalMonthly)}
                </p>
                <div className="flex justify-center gap-8 text-sm text-white/80">
                  <div>
                    <p className="font-semibold text-white">{formatCurrency(monthlyMortgage)}</p>
                    <p>Principal & Interest</p>
                  </div>
                  <div>
                    <p className="font-semibold text-white">{formatCurrency(monthlyTax)}</p>
                    <p>Property Tax</p>
                  </div>
                  <div>
                    <p className="font-semibold text-white">{formatCurrency(monthlyInsurance)}</p>
                    <p>Insurance</p>
                  </div>
                </div>
              </div>

              {/* Breakdown Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-sm p-5">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <DollarSign className="w-4 h-4" />
                    <span className="text-sm">Loan Amount</span>
                  </div>
                  <p className="font-mono text-2xl font-semibold" data-testid="loan-amount">
                    {formatCurrency(loanAmount)}
                  </p>
                </div>
                <div className="bg-card border border-border rounded-sm p-5">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-sm">Total Interest</span>
                  </div>
                  <p className="font-mono text-2xl font-semibold text-gold" data-testid="total-interest">
                    {formatCurrency(totalInterest)}
                  </p>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-muted rounded-sm p-6">
                <h3 className="font-serif text-lg mb-4">Loan Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Home Price</span>
                    <span className="font-medium">{formatCurrency(homePrice)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Down Payment</span>
                    <span className="font-medium">- {formatCurrency(downPayment)}</span>
                  </div>
                  <div className="border-t border-border my-2"></div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Loan Amount</span>
                    <span className="font-medium">{formatCurrency(loanAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Interest ({loanTerm} years)</span>
                    <span className="font-medium">{formatCurrency(totalInterest)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Taxes & Insurance</span>
                    <span className="font-medium">
                      {formatCurrency((propertyTax + insurance) * parseInt(loanTerm))}
                    </span>
                  </div>
                  <div className="border-t border-border my-2"></div>
                  <div className="flex justify-between">
                    <span className="font-medium">Total Cost of Loan</span>
                    <span className="font-mono font-bold text-lg text-primary">
                      {formatCurrency(totalPayments)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <p className="text-xs text-muted-foreground">
                * This calculator provides estimates for informational purposes only. 
                Actual rates and terms may vary based on credit score, lender, and other factors. 
                Consult with a mortgage professional for accurate quotes.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MortgageCalculator;
